import React, { useEffect, useState } from 'react';
import {
  getNotifications, markAllNotificationsRead, markNotificationRead,
  deleteNotification, deleteNotifications, clearAllNotifications,
} from '../api';
import { Bell, CheckCheck, AlertTriangle, ArrowUpCircle, Download, Wifi, Trash2, FileX, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/Button';

const META = {
  site_down:        { icon: AlertTriangle, cls: 'red',   bg: 'var(--red-bg)',    color: 'var(--red)' },
  site_up:          { icon: Wifi,          cls: 'green', bg: 'var(--green-bg)',  color: 'var(--green)' },
  update_available: { icon: Download,      cls: 'amber', bg: 'var(--amber-bg)',  color: 'var(--amber)' },
  update_result:    { icon: ArrowUpCircle, cls: 'blue',  bg: 'var(--accent-bg)', color: 'var(--accent)' },
  form_cleanup:     { icon: FileX,         cls: 'blue',  bg: 'var(--accent-bg)', color: 'var(--accent)' },
  site_issue:       { icon: AlertTriangle, cls: 'amber', bg: 'var(--amber-bg)',  color: 'var(--amber)' },
  test:             { icon: Bell,          cls: 'blue',  bg: 'var(--accent-bg)', color: 'var(--accent)' },
};

const timeAgo = (d) => {
  const s = Math.floor((Date.now() - new Date(d)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);

  const load = () => getNotifications(50)
    .then(list => { setItems(list); setSelected(new Set()); })
    .catch(console.error)
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const fireBadgeRefresh = () => window.dispatchEvent(new Event('wpm:notifications-read'));

  const handleReadAll = async () => {
    await markAllNotificationsRead();
    setItems(prev => prev.map(n => ({ ...n, read: true })));
    fireBadgeRefresh();
    toast.success('All marked as read');
  };

  const handleRead = async (n) => {
    if (n.read) return;
    await markNotificationRead(n.id);
    setItems(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
    fireBadgeRefresh();
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    await deleteNotification(id);
    setItems(prev => prev.filter(x => x.id !== id));
    setSelected(prev => { const next = new Set(prev); next.delete(id); return next; });
    fireBadgeRefresh();
  };

  const toggleOne = (e, id) => {
    e.stopPropagation(); // picking a row must not also mark it read
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allSelected = items.length > 0 && selected.size === items.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map(n => n.id)));

  const handleDeleteSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} notification${ids.length !== 1 ? 's' : ''}?`)) return;
    setDeleting(true);
    try {
      const r = await deleteNotifications(ids);
      setItems(prev => prev.filter(x => !selected.has(x.id)));
      setSelected(new Set());
      fireBadgeRefresh();
      toast.success(`${r.deleted ?? ids.length} notification(s) deleted`);
    } catch (err) { toast.error(err?.error || 'Could not delete'); }
    finally { setDeleting(false); }
  };

  /* Clears everything on the server, including anything past the 50 shown. */
  const handleClearAll = async () => {
    if (!window.confirm('Delete every notification?\n\nThis clears the whole list, including any older ones not shown on this page. It cannot be undone.')) return;
    setDeleting(true);
    try {
      const r = await clearAllNotifications();
      setItems([]);
      setSelected(new Set());
      fireBadgeRefresh();
      toast.success(`${r.deleted ?? 0} notification(s) deleted`);
    } catch (err) { toast.error(err?.error || 'Could not clear the list'); }
    finally { setDeleting(false); }
  };

  const unread = items.filter(n => !n.read).length;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Notifications</h1>
          <p>{unread > 0 ? `${unread} unread` : 'You’re all caught up'}</p>
        </div>
        <div className="header-actions">
          <button className="btn" onClick={handleReadAll} disabled={!unread}>
            <CheckCheck size={14} /> Mark all read
          </button>
          <Button className="btn danger" onClick={handleClearAll} loading={deleting} disabled={!items.length}>
            <Trash2 size={14} /> Clear all
          </Button>
        </div>
      </div>

      <div className="page-content">
        <div className="card">
          {loading ? (
            <div style={{ padding: 28, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Loading…</div>
          ) : items.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>
              <Bell size={26} style={{ marginBottom: 8, opacity: .5 }} />
              <div style={{ fontSize: 13 }}>No notifications yet</div>
            </div>
          ) : (
            <div>
              {/* Selection bar — select all, and act on what is picked. */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 18px',
                borderBottom: '1px solid var(--border)',
                background: selected.size ? 'var(--accent-bg)' : 'transparent',
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', fontSize: 12, color: 'var(--text2)' }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll}
                    ref={el => { if (el) el.indeterminate = selected.size > 0 && !allSelected; }}
                    style={{ accentColor: 'var(--accent)', width: 15, height: 15, cursor: 'pointer' }}
                    aria-label="Select all notifications" />
                  {selected.size ? `${selected.size} selected` : 'Select all'}
                </label>

                {selected.size > 0 && (
                  <>
                    <Button className="btn sm danger" onClick={handleDeleteSelected} loading={deleting}>
                      <Trash2 size={12} /> Delete selected
                    </Button>
                    <button className="btn sm" onClick={() => setSelected(new Set())}>
                      <X size={12} /> Clear selection
                    </button>
                  </>
                )}
              </div>

              {items.map(n => {
                const m = META[n.type] || META.test;
                const Icon = m.icon;
                return (
                  <div key={n.id} onClick={() => handleRead(n)}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 13, padding: '13px 18px',
                      borderBottom: '1px solid var(--border)', cursor: n.read ? 'default' : 'pointer',
                      background: selected.has(n.id) ? 'var(--bg3)' : (n.read ? 'transparent' : 'var(--accent-bg)'),
                      transition: 'background .15s',
                    }}>
                    <input type="checkbox" checked={selected.has(n.id)}
                      onChange={(e) => toggleOne(e, n.id)} onClick={(e) => e.stopPropagation()}
                      style={{ accentColor: 'var(--accent)', width: 15, height: 15, marginTop: 11, flexShrink: 0, cursor: 'pointer' }}
                      aria-label={`Select: ${n.title}`} />
                    <div style={{ width: 36, height: 36, borderRadius: 11, flexShrink: 0, background: m.bg, color: m.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={18} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{n.title}</span>
                        {!n.read && <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3 }}>{n.message}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 5 }}>
                        {n.site_name ? `${n.site_name} · ` : ''}{timeAgo(n.created_at)}
                      </div>
                    </div>
                    <button className="btn sm" onClick={(e) => handleDelete(e, n.id)} style={{ flexShrink: 0 }} title="Delete">
                      <Trash2 size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
